import React, { useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";
import { buildMpptRows, hasExplicitMppt } from "./InverterMpptSummary";
import { INVERTER_BATTERY_SECTION, INVERTER_CATALOGUE_SECTIONS, catalogueLinks } from "./inverterCataloguePresentation";

/**
 * The selected component's catalogue record, categorised.
 *
 * The details dialogs showed one long scroll: an overview card, then a run of
 * sections, then whatever did not fit was not shown at all. An expert reading
 * it cannot find a coefficient without hunting, and cannot tell whether a field
 * is absent from the record or merely absent from the view.
 *
 * PVsyst answers both with tabs -- Basic data, Sizes and Technology, Model
 * parameters, Commercial -- so the shape of the record is visible before any
 * value is read. Only reviewed presentation fields are shown. Unknown database
 * keys remain stored, but are not exposed as user-facing labels.
 *
 * Nothing here computes. Derived figures belong beside the selector, where the
 * decision is made; this is the record as stored.
 */

const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const out = Number(String(value).replace(",", "."));
  return Number.isFinite(out) ? out : null;
};

/**
 * First key present, so one row can accept the several spellings in use.
 *
 * Own properties only. Reading a key straight off the object also finds
 * whatever the prototype carries -- an array answers `length` with its element
 * count, so a stray array reached the module's "Length" row and reported
 * 0.000 m as though the catalogue held it. A catalogue record is a bag of
 * strings and numbers; anything it did not store itself is not a value.
 */
function firstPresent(chars, keys) {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(chars, key)) continue;
    const raw = chars[key];
    if (raw !== undefined && raw !== null && raw !== "") return { key, raw };
  }
  return null;
}

/**
 * A stored flag, read out as an answer.
 *
 * Catalogue records spell a yes/no field however the importer that wrote them
 * felt like: 1, true, "Y", "yes". Printed through the numeric path that came
 * out as a bare "1" in the Bifacial row, which is a value nobody can read
 * without knowing the convention -- and 1 sitting directly above a bifaciality
 * of 0.70 invites reading it as a second coefficient.
 *
 * A spelling not on either list is shown as stored rather than guessed at: a
 * field the catalogue holds in some form this does not recognise is a thing to
 * notice, not to paper over with a confident "No".
 */
const TRUTHY = new Set(["1", "true", "yes", "y", "t"]);
const FALSY = new Set(["0", "false", "no", "n", "f"]);

function formatBoolean(raw) {
  const token = String(raw).trim().toLowerCase();
  if (TRUTHY.has(token)) return "Yes";
  if (FALSY.has(token)) return "No";
  return String(raw);
}

function formatValue(raw, unit, digits, boolean, locale) {
  if (boolean) return formatBoolean(raw);
  const n = num(raw);
  if (n === null) return String(raw);
  const shown =
    digits === undefined
      ? n.toLocaleString("en-GB", { maximumFractionDigits: 4 })
      : n.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return unit ? `${shown} ${unit}` : shown;
}

/**
 * Four tabs each, and tables inside them.
 *
 * The first cut gave every heading its own tab: seven for a module, and the
 * strip needed arrows to reach the last two, which is worse than the scroll it
 * replaced -- a tab you have to hunt for hides its contents as surely as a
 * section further down a page. PVsyst fits its tabs across the dialog and puts
 * several framed tables inside each. Same here: the tab is the subject, the
 * tables inside it are the headings.
 */
const MODULE_TABS = [
  {
    label: "Electrical",
    sections: [
      {
        title: "At standard test conditions",
        rows: [
          { label: "Nominal power", keys: ["STC", "Pmp0", "Pmp"], unit: "Wp", digits: 0 },
          { label: "Voc", keys: ["V_oc_ref", "Voc_ref", "Voc0", "Voco"], unit: "V", digits: 2 },
          { label: "Vmp", keys: ["V_mp_ref", "Vmp_ref", "Vmp0", "Vmpo"], unit: "V", digits: 2 },
          { label: "Isc", keys: ["I_sc_ref", "Isc_ref", "Isc0", "Isco"], unit: "A", digits: 2 },
          { label: "Imp", keys: ["I_mp_ref", "Imp_ref", "Imp0", "Impo"], unit: "A", digits: 2 },
          { label: "Cells in series", keys: ["N_s", "Cells_in_Series", "cells_in_series"], digits: 0 },
          { label: "Maximum system voltage", keys: ["Maximum System Voltage", "Vsysmax", "V_system_max", "max_system_voltage"], unit: "V", digits: 0 },
          { label: "Bypass diodes", keys: ["bypass_diodes", "Bypass diodes", "BypassDiodes"], digits: 0 },
        ],
      },
      {
        title: "Temperature coefficients",
        column: "right",
        // Every row here carries a unit, and for two of them the unit depends
        // on which spelling the record used -- a row that accepts a CEC name
        // and a Sandia name is accepting two different quantities:
        //
        //   alpha_sc  (CEC)     amps per degree
        //   Aisc      (Sandia)  normalised, i.e. a FRACTION of Isc per degree
        //   gamma_r   (CEC)     percent per degree
        //   gamma_pdc (PVWatts) a fraction per degree -- a hundredth of the above
        //
        // Isc had no unit at all, which is the worst of the options: 0.0063
        // beside a 13.98 A module is 0.045 %/C if it is amps and 0.63 %/C if it
        // is a fraction, and nothing on screen said which.
        rows: [
          { label: "Voc (\u03b2oc)", keys: ["beta_oc", "Bvoco", "beta_voc"], unit: "V/\u00b0C" },
          {
            label: "Isc (\u03b1sc)",
            keys: ["alpha_sc", "Aisc", "alpha_isc"],
            unit: "A/\u00b0C",
            units: { Aisc: "1/\u00b0C" },
          },
          {
            label: "Vmp",
            keys: ["Bvmpo", "Bvmp", "beta_vmp", "gamma_vmp"],
            // Bvmpo/Bvmp/beta_vmp are VOLTS per degree, the same convention as
            // beta_oc above; gamma_vmp is a percent. Printing the first three
            // as "%/C" was the label agreeing with a bug that string_limits.py
            // no longer has.
            unit: "V/\u00b0C",
            units: { gamma_vmp: "%/\u00b0C" },
          },
          {
            label: "Power (\u03b3)",
            keys: ["gamma_r", "gamma_pdc", "gamma_pmp"],
            unit: "%/\u00b0C",
            units: { gamma_pdc: "1/\u00b0C" },
          },
          { label: "NOCT", keys: ["T_NOCT", "NOCT", "Tnoct"], unit: "\u00b0C", digits: 1 },
        ],
      },
    ],
  },
  {
    label: "Physical",
    sections: [
      {
        title: "Dimensions",
        rows: [
          { label: "Length", keys: ["Length", "length"], unit: "m", digits: 3 },
          { label: "Width", keys: ["Width", "width"], unit: "m", digits: 3 },
          { label: "Module area", keys: ["Area", "area", "module_area"], unit: "m\u00b2", digits: 3 },
        ],
      },
      {
        title: "Technology",
        column: "right",
        rows: [
          { label: "Cell technology", keys: ["Technology", "Material", "technology"] },
          { label: "Bifacial", keys: ["Bifacial", "bifacial"], boolean: true },
          { label: "Bifaciality", keys: ["Bifaciality", "bifaciality"], digits: 2 },
        ],
      },
    ],
  },
  {
    label: "Model & catalogue",
    sections: [
      {
        title: "Single-diode model",
        rows: [
          { label: "Modified diode ideality factor", labelEl: "Τροποποιημένος συντελεστής ιδανικότητας διόδου", keys: ["a_ref"], unit: "V" },
          { label: "Model reference area", labelEl: "Επιφάνεια αναφοράς μοντέλου", keys: ["A_c"], unit: "m\u00b2", digits: 3 },
          { label: "Reference photocurrent", labelEl: "Φωτορεύμα αναφοράς", keys: ["I_L_ref"], unit: "A" },
          { label: "Reference diode saturation current", labelEl: "Ρεύμα κόρου διόδου αναφοράς", keys: ["I_o_ref"], unit: "A" },
          { label: "Series resistance", labelEl: "Αντίσταση σειράς", keys: ["R_s"], unit: "\u03a9" },
          { label: "Reference shunt resistance", labelEl: "Παράλληλη αντίσταση αναφοράς", keys: ["R_sh_ref"], unit: "\u03a9" },
          { label: "Temperature-coefficient adjustment", labelEl: "Διόρθωση θερμοκρασιακού συντελεστή", keys: ["Adjust"], unit: "%" },
        ],
      },
      {
        title: "Commercial",
        column: "right",
        rows: [
          { label: "Manufacturer", keys: ["Vendor", "vendor", "Manufacturer", "manufacturer", "Maker"] },
          { label: "Unit price", keys: ["Price", "price"], unit: "\u20ac", digits: 2 },
        ],
      },
    ],
  },
];

const INVERTER_TABS = [
  {
    label: "Electrical",
    sections: [
      // Which column each table sits in, rather than whatever the browser's
      // balancing arrives at. Left-hand column first, so the DC side -- the
      // half a designer is actually sizing strings against -- leads, and the
      // five-row AC summary sits opposite it.
      {
        title: "AC output",
        column: "right",
        rows: [
          { label: "Rated power (Paco)", keys: ["Paco"], unit: "W", digits: 0 },
          { label: "Maximum apparent power", labelEl: "Μέγιστη φαινόμενη ισχύς", keys: ["max_ac_apparent_va"], unit: "VA", digits: 0 },
          { label: "Voltage", keys: ["Vac"], unit: "V", digits: 0 },
          { label: "Phases", keys: ["Phases", "phases"], digits: 0 },
          { label: "Nominal efficiency", keys: ["eff_nom"], digits: 4 },
          { label: "Maximum efficiency", keys: ["eff_max"], digits: 4 },
        ],
      },
      {
        title: "DC input",
        rows: [
          { label: "Reference power (Pdco)", keys: ["Pdco"], unit: "W", digits: 0 },
          { label: "Maximum power (Pdcmax)", keys: ["Pdcmax"], unit: "W", digits: 0 },
          { label: "Reference voltage (Vdco)", keys: ["Vdco"], unit: "V", digits: 0 },
          { label: "Maximum voltage (Vdcmax)", keys: ["Vdcmax"], unit: "V", digits: 0 },
          { label: "Maximum current (Idcmax)", keys: ["Idcmax"], unit: "A", digits: 1 },
        ],
      },
      {
        title: "MPPT trackers",
        rows: [
          { label: "Window, lower bound", keys: ["Mppt_low", "MPPT_low"], unit: "V", digits: 0 },
          { label: "Window, upper bound", keys: ["Mppt_high", "MPPT_high"], unit: "V", digits: 0 },
          { label: "Trackers", keys: ["Num MPPTs", "numMPPT"], digits: 0 },
          { label: "String inputs per tracker", keys: ["Num Inputs per MPPT", "numInptMPPT"], digits: 0 },
          { label: "Maximum current per tracker", keys: ["Idcmax_per_mppt"], unit: "A", digits: 1 },
        ],
      },
    ],
  },
  {
    label: "Conversion model",
    sections: [
      {
        title: "Sandia coefficients",
        rows: [
          { label: "Power-curve coefficient (C0)", labelEl: "Συντελεστής καμπύλης ισχύος (C0)", keys: ["C0"] },
          { label: "DC power voltage-dependence (C1)", labelEl: "Εξάρτηση ισχύος DC από την τάση (C1)", keys: ["C1"] },
          { label: "Starting-power voltage-dependence (C2)", labelEl: "Εξάρτηση ισχύος εκκίνησης από την τάση (C2)", keys: ["C2"] },
          { label: "Curvature voltage-dependence (C3)", labelEl: "Εξάρτηση καμπυλότητας από την τάση (C3)", keys: ["C3"] },
        ],
      },
      {
        title: "Thresholds",
        column: "right",
        rows: [
          { label: "Starting power (Pso)", keys: ["Pso"], unit: "W", digits: 1 },
          { label: "Night consumption (Pnt)", keys: ["Pnt"], unit: "W", digits: 1 },
        ],
      },
    ],
  },
  {
    label: "Catalogue",
    sections: [
      {
        title: "Commercial",
        rows: [
          { label: "Manufacturer", keys: ["Vendor", "vendor", "Manufacturer", "manufacturer", "Maker"] },
          { label: "Unit price", keys: ["Price", "price"], unit: "\u20ac", digits: 2 },
        ],
      },
    ],
  },
];

/**
 * Resolve the tabs against one record.
 *
 * Exported because the interesting behaviour is here, not in the markup: a row
 * whose value is absent is dropped, a section with no surviving row is dropped,
 * a tab with no surviving section is dropped. Unmapped keys are not displayed;
 * this is a customer-facing catalogue, not a raw database inspector.
 */
export function buildSpecTabs(kind, chars, locale, catalogAccess = null) {
  const source =
    chars && typeof chars === "object" && !Array.isArray(chars) ? chars : {};
  const definitions = kind === "inverter" ? INVERTER_TABS : MODULE_TABS;
  const tabs = [];
  const explicitMppt = kind === "inverter" && hasExplicitMppt(source);
  const greek = locale?.startsWith("el");
  const indicativePrice = source.catalog_source?.price_estimate?.status === "indicative";

  definitions.forEach((tab) => {
    const sections = [];
    const sectionsToShow = [...tab.sections];
    if (kind === "inverter" && tab.label === "Electrical") sectionsToShow.push(INVERTER_BATTERY_SECTION);
    if (kind === "inverter" && tab.label === "Catalogue") sectionsToShow.push(...INVERTER_CATALOGUE_SECTIONS);
    sectionsToShow.forEach((section) => {
      const rows = [];
      section.rows.forEach((row) => {
        if (explicitMppt && ["Num Inputs per MPPT", "Idcmax_per_mppt"].includes(row.keys[0])) {
          return;
        }
        const hit = firstPresent(source, row.keys);
        if (!hit) return;
        const label = row.label === "Unit price" && indicativePrice
          ? (greek ? "Ενδεικτική τιμή μονάδας" : "Indicative unit price")
          : (greek ? row.labelEl || row.label : row.label);
        if (row.links) {
          const links = catalogueLinks(hit.raw, greek);
          if (links.length) rows.push({ label, value: links.map((link) => link.label).join(", "), links });
          return;
        }
        rows.push({
          label,
          // The unit can depend on WHICH spelling the record used: a row that
          // accepts both a CEC and a Sandia name is accepting two different
          // quantities. See the `units` note on the temperature rows.
          value: row.choices
            ? row.choices[hit.raw]?.[greek ? 1 : 0] || (greek ? "Μη αναγνωρισμένη περιγραφή πηγής" : "Unrecognized source description")
            : formatValue(
            hit.raw,
            (row.units && row.units[hit.key]) || row.unit,
            row.digits,
            row.boolean,
            locale
          ),
        });
      });
      if (explicitMppt && section.title === "MPPT trackers") {
        rows.push(...buildMpptRows(source, greek));
      }
      if (kind === "inverter" && section.title === "MPPT trackers") {
        [
          ["Idcmax_per_input", greek ? "Μέγιστο ρεύμα ανά φυσική είσοδο" : "Maximum current per physical input", "A"],
          ["mppt_isc_stc_multiplier", greek ? "Συντελεστής σχεδιασμού Isc(STC)" : "Isc(STC) design multiplier", "×"],
        ].forEach(([key, label, unit]) => {
          if (source[key] != null) {
            rows.push({ label, value: formatValue(source[key], unit, undefined, false, locale) });
          }
        });
      }
      if (section.title === "Commercial" && indicativePrice) {
        const estimate = source.catalog_source.price_estimate;
        if (estimate.evidence_url) rows.push({
          label: greek ? "Πηγή ενδεικτικής τιμής" : "Indicative price source",
          value: greek ? "Προβολή πηγής" : "View source",
          links: [{ url: estimate.evidence_url, label: greek ? "Προβολή πηγής" : "View source" }],
        });
      }
      if (rows.length) sections.push({
        title: greek ? section.titleEl || section.title : section.title,
        column: section.column, rows,
        ...(section.title === "Commercial" && indicativePrice
          ? { note: greek
            ? "Ενδεικτική τιμή χωρίς ΦΠΑ, όχι προσφορά. Ελέγξτε και προσαρμόστε την τιμή για το έργο."
            : "Indicative price excluding VAT, not a quote. Check and adjust the price for this project." }
          : section.note ? { note: greek ? section.noteEl || section.note : section.note } : {}),
      });
    });
    if (tab.label === "Catalogue" || tab.label === "Model & catalogue") {
      const rows = [];
      if (catalogAccess?.owner !== undefined) {
        rows.push({
          label: greek ? "Ιδιοκτήτης" : "Owner",
          value: String(catalogAccess.owner || "").trim().toLowerCase() === "global"
            ? (greek ? "Κοινόχρηστος κατάλογος" : "Shared catalogue")
            : catalogAccess.owner || (greek ? "Δεν έχει οριστεί" : "Not specified"),
        });
        const visibilityLabels = {
          global: greek ? "Όλοι οι οργανισμοί" : "All organizations",
          selected: greek ? "Επιλεγμένοι οργανισμοί" : "Selected organizations",
          organization: greek ? "Μόνο ο οργανισμός ιδιοκτήτης" : "Owner organization only",
        };
        rows.push({
          label: greek ? "Ορατό σε" : "Visible to",
          value: visibilityLabels[catalogAccess.visibility] || (greek ? "Δεν έχει οριστεί" : "Not specified"),
        });
        if (catalogAccess.visibility === "selected") {
          rows.push({
            label: greek ? "Οργανισμοί" : "Organizations",
            value: catalogAccess.organizations?.join(", ") || (greek ? "Δεν έχουν οριστεί" : "Not specified"),
          });
        }
      } else if (catalogAccess?.category) {
        const categoryLabels = {
          own: greek ? "Του οργανισμού μου" : "My organization's equipment",
          global: greek ? "Κοινόχρηστο για όλους" : "Shared with all organizations",
          shared: greek ? "Κοινόχρηστο με τον οργανισμό μου" : "Shared with my organization",
        };
        if (categoryLabels[catalogAccess.category]) {
          rows.push({ label: greek ? "Διαθεσιμότητα" : "Availability", value: categoryLabels[catalogAccess.category] });
        }
      }
      if (rows.length) sections.push({
        title: greek ? "Πρόσβαση στον κατάλογο" : "Catalogue access",
        column: "right",
        rows,
      });
    }
    if (sections.length) tabs.push({ label: tab.label, sections });
  });

  return tabs;
}

function SpecTable({ title, rows, note }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        mb: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          px: 1.25,
          py: 0.7,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "0.68rem",
          color: "text.secondary",
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ px: 1.25 }}>
        {rows.map((row, index) => (
          <Box
            key={row.label}
            sx={{
              display: "grid",
              gridTemplateColumns: row.value.length > 60 ? "minmax(0, 1fr)" : "minmax(0, 1.4fr) minmax(0, 1fr)",
              gap: row.value.length > 60 ? 0.5 : 1.5,
              py: 0.6,
              borderTop: index === 0 ? "none" : "1px solid",
              borderColor: "divider",
              alignItems: "baseline",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                textAlign: row.value.length > 60 ? "left" : "right",
                overflowWrap: "anywhere",
                lineHeight: 1.4,
              }}
            >
              {row.links ? row.links.map((link) => (
                <Box key={link.url} component="a" href={link.url} target="_blank" rel="noopener noreferrer" sx={{ display: "block", color: "primary.main" }}>
                  {link.label}
                </Box>
              )) : row.value}
            </Typography>
          </Box>
        ))}
      </Box>
      {note && <Typography variant="caption" sx={{ display: "block", px: 1.25, pb: 1, color: "text.secondary" }}>{note}</Typography>}
    </Box>
  );
}

/**
 * How a panel is shown or withheld.
 *
 * Exported so the choice can be asserted rather than eyeballed, because the
 * wrong choice here is invisible in a screenshot and obvious in use: anything
 * that removes an inactive panel from layout - unmounting it, or
 * `display: none` - leaves the container with nothing to take its height from,
 * and the dialog then resizes on every click, walking the Close button up and
 * down the screen while someone is reading.
 *
 * `visibility: hidden` keeps the box in the flow. All panels share one grid
 * cell, so the cell is as tall as the tallest of them and stays there for the
 * life of the dialog - a fixed frame, as in PVsyst, arrived at without
 * measuring anything or hard-coding a height that a longer record would break.
 */
export function panelStyle(isActive) {
  return {
    gridArea: "1 / 1",
    // A grid item's minimum is its content's by default, so a panel could
    // push the dialog wider than its 680px and leave a horizontal scrollbar
    // under content that fits.
    minWidth: 0,
    visibility: isActive ? "visible" : "hidden",
    pointerEvents: isActive ? "auto" : "none",
  };
}

// A catalogue record can have only commercial/access details. In that case
// the right-hand group occupies the first grid cell instead of leaving it blank.
export function specSectionSides(sections) {
  return sections.some((section) => (section.column || "left") === "left")
    ? ["left", "right"]
    : ["right"];
}

export default function EquipmentSpecTabs({ kind, chars, catalogAccess = null, overview = null }) {
  const greek = useIsGreek();
  const tabs = useMemo(
    () => buildSpecTabs(kind, chars, greek ? "el-GR" : "en-US", catalogAccess),
    [kind, chars, greek, catalogAccess]
  );
  const [tab, setTab] = useState(0);

  const panels = [
    ...(overview ? [{ label: "Overview", content: overview }] : []),
    ...tabs.map((entry) => ({
      label: entry.label,
      // Two columns, which is what the 680px dialog has room for.
      //
      // The pair is bounded at both ends and 680/two columns is the balance
      // point between them: much wider and the tabs are mostly margin, much
      // narrower and the longest labels ("String inputs per tracker") break
      // over two lines, which costs back the height the columns saved. One
      // stacked column does go narrower -- to about 560 -- but the dialog then
      // reads as a tall slot, so the width came back here instead.
      content: (
        // Two declared columns, not CSS multi-column.
        //
        // Multi-column balances by height and will not split a table, so it
        // put the one five-row table on the left and stacked the other two on
        // the right -- a decision made by the browser, about which half of an
        // inverter a reader sees first. Each section now names its side.
        //
        // columnGap goes through the theme's spacing scale, so 3 is 24px. It
        // was written as 16 meaning pixels, which MUI read as 16 * 8px = 128px
        // -- a trough wider than either table.
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
            columnGap: 3,
            alignItems: "start",
          }}
        >
          {specSectionSides(entry.sections).map((side) => (
            <Box key={side}>
              {entry.sections
                .filter((section) => (section.column || "left") === side)
                .map((section) => (
                  <SpecTable key={section.title} title={section.title} rows={section.rows} note={section.note} />
                ))}
            </Box>
          ))}
        </Box>
      ),
    })),
  ];

  if (panels.length === 0) return null;
  const active = Math.min(tab, panels.length - 1);

  return (
    // Clipped sideways: the tables wrap their values, so nothing here is meant
    // to be wider than the dialog, and a few stray pixels from a hidden panel
    // showed as a horizontal scrollbar. "clip" rather than "hidden", which
    // would turn the vertical axis into a scroll container of its own.
    <Box sx={{ overflowX: "clip" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1.75 }}>
        {/* Full width, never scrollable: with four short labels they fit the
            dialog, and arrows on a tab strip are a navigation failure. */}
        <Tabs
          value={active}
          onChange={(_, next) => setTab(next)}
          variant="fullWidth"
        >
          {panels.map((panel) => (
            <Tab
              key={panel.label}
              label={panel.label}
              sx={{ textTransform: "none", minHeight: 42, fontWeight: 600, fontSize: "0.86rem" }}
            />
          ))}
        </Tabs>
      </Box>
      {/* One cell, every panel in it, only one of them visible. See panelStyle
          for why they are all rendered and none of them is display:none. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}>
        {panels.map((panel, index) => (
          <Box
            key={panel.label}
            role="tabpanel"
            // Hidden panels are still in the document, so they are kept out of
            // the accessibility tree and out of reach of the pointer.
            aria-hidden={index !== active}
            inert={index !== active}
            sx={panelStyle(index === active)}
          >
            {panel.content}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
